import io, { Socket } from "socket.io-client";
import { addPipelineToSocketPayload } from "./canvasUrlParams";
import { isPublicSlug, fetchAndStorePublicToken } from "./publicUtils";
import {
  refreshAuthTokenWithOutcome,
  onTokenRefreshed,
  isAuthTokenExpiring,
  notifySessionDisconnected,
} from "./tokenRefresh";
import { addScanModeToSocketPayload } from "./scanMode";

// Define a type for the custom listener map
interface ListenerMap {
  [event: string]: Array<(...args: any[]) => void>;
}

// Reconnect budget for one connection loss, replenished on a successful connect or token refresh.
const MAX_RECONNECT_ATTEMPTS = 5;

class WebSocketInstance {
  private static instance: WebSocketInstance | null = null;
  private socketRef: Socket | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay: number = 15000;
  private customListeners: ListenerMap = {};
  private reconnectAttempts: number = MAX_RECONNECT_ATTEMPTS;
  // Guards against a refresh loop when the refreshed token is itself rejected at connect.
  private isRefreshingToken: boolean = false;
  // Removes this instance's token-refresh subscription in close(). Without it a retired instance
  // keeps reconnecting (and re-attaching its old listeners) on every later refresh, so each
  // logout/login cycle would leave another duplicate socket behind.
  private unsubscribeTokenRefresh: (() => void) | null = null;

  private constructor() {
    // A socket authenticates once at connect time, so a refreshed token can only be adopted by
    // reconnecting. connect() detects the changed token and swaps the connection.
    this.unsubscribeTokenRefresh = onTokenRefreshed(() => {
      if (localStorage.getItem("JMLAuthToken")) this.connect();
    });
  }

  public static getInstance(): WebSocketInstance {
    if (!WebSocketInstance.instance) {
      WebSocketInstance.instance = new WebSocketInstance();
    }
    if (!WebSocketInstance.instance.socketRef)
      WebSocketInstance.instance.connect();
    return WebSocketInstance.instance;
  }

  public connect(): void {
    const path = `${process.env.REACT_APP_SIMPLIFAI_BACKEND_WSS_URL}`;
    const isDevEnvironment = process.env.REACT_APP_ENVIRONMENT === "dev";
    const token = localStorage.getItem("JMLAuthToken") || localStorage.getItem("JMLPublicToken");

    if (!token) {
      isDevEnvironment && console.log("No token found, skipping connect");
      return;
    }

    if (this.socketRef) {
      const currentToken = (this.socketRef as any).io?.opts?.query?.token;
      if (currentToken === token) {
        isDevEnvironment && console.log("Socket already connected with the same token");
        return;
      }
      isDevEnvironment && console.log("Token changed, reconnecting...");
      this.socketRef.disconnect();
      this.socketRef = null;
    }

    isDevEnvironment && console.log("JMLAuthToken (or Public): ", token.substring(0, 10) + "...");

    // Never open a connection with an already-expired user token: the server would reject it and
    // every reconnect attempt would replay the same dead credential. Refresh first instead.
    const isUserToken = !!localStorage.getItem("JMLAuthToken");
    if (isUserToken && isAuthTokenExpiring(0)) {
      isDevEnvironment && console.log("Token has expired, refreshing before connect");
      void this.refreshAndReconnect();
      return;
    }

    const socket = io(path, {
      query: { token },
    });
    this.socketRef = socket;

    socket.on("connect", () => {
      if (this.socketRef !== socket) return;
      isDevEnvironment && console.log("Successfully connected to socket");
      this.attachCustomListeners();
      this.reconnectAttempts = MAX_RECONNECT_ATTEMPTS;
    });
    socket.on("disconnect", () => {
      if (this.socketRef !== socket) return;
      isDevEnvironment && console.log("Disconnected from socket");
      this.socketRef = null;
      this.attemptReconnect();
    });
    socket.on("connect_error", (error: any) => {
      if (this.socketRef !== socket) return;
      const isDevEnvironment = process.env.REACT_APP_ENVIRONMENT === "dev";
      isDevEnvironment && console.error("WebSocket connection error:", error);
      if (error.message) {
        isDevEnvironment && console.error("Error Message:", error.message);
      }
      if (error.stack) {
        isDevEnvironment && console.error("Error Stack:", error.stack);
      }
      this.socketRef = null;
      this.attemptReconnect();
    });
    // Server disconnected us because the token backing this socket hit its exp claim.
    // Refresh and reconnect rather than logging out — the session itself is still valid.
    socket.on("token_expired", () => {
      if (this.socketRef !== socket) return;
      socket.disconnect();
      this.socketRef = null;
      void this.refreshAndReconnect();
    });
    socket.on("auth_error", (errorData: any) => {
      if (this.socketRef !== socket) return;
      socket.disconnect();
      this.socketRef = null;
      const JMLAuthToken = localStorage.getItem("JMLAuthToken");
      if (JMLAuthToken) {
        // The token may simply have expired between reads; try a refresh before logging out.
        void this.refreshAndReconnect();
      } else {
        localStorage.removeItem("JMLPublicToken");
        localStorage.removeItem("JMLPublicTokenExpiry");
      }
    });
    socket.on("access_error", (errorData: any) => {
      if (this.socketRef !== socket) return;
      socket.disconnect();
      this.socketRef = null;
      const JMLAuthToken = localStorage.getItem("JMLAuthToken");
      if (JMLAuthToken) {
        localStorage.removeItem("JMLAuthToken");
        localStorage.removeItem("JMLFirstname");
        localStorage.removeItem("JMLLastname");
        window.location.href = "/login";
      } else {
        localStorage.removeItem("JMLPublicToken");
        localStorage.removeItem("JMLPublicTokenExpiry");
      }
    });
  }

  /**
   * Refresh the access token and reconnect the socket with it.
   * Prompts for a re-login only when the backend actually refuses the refresh token, which means
   * the session is genuinely over. An unreachable backend goes back through the reconnect schedule
   * instead — the credentials are probably still good.
   */
  private async refreshAndReconnect(): Promise<void> {
    if (this.isRefreshingToken) return;
    this.isRefreshingToken = true;
    try {
      const outcome = await refreshAuthTokenWithOutcome();
      if (outcome.status === "refreshed") {
        // Restore the reconnect budget: this was an expiry, not an unreachable server.
        this.reconnectAttempts = MAX_RECONNECT_ATTEMPTS;
        this.connect();
        return;
      }
      if (outcome.status === "unavailable") {
        // Backend unreachable, not a refused credential. Retry on the normal schedule; the
        // disconnect notice is only raised once that budget is exhausted.
        this.attemptReconnect();
        return;
      }
      // The refresh token was refused (or is gone): the session is over. Tell the user and let
      // them acknowledge; the logout runs on their click.
      notifySessionDisconnected();
    } finally {
      this.isRefreshingToken = false;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts > 0) {
      const isDevEnvironment = process.env.REACT_APP_ENVIRONMENT === "dev";
      isDevEnvironment && console.log("Attempting to reconnect...");
      this.reconnectAttempts--;
      // Replace any pending attempt rather than stacking a second timer on top of it.
      if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = setTimeout(() => {
        this.reconnectTimeout = null;
        this.connect();
      }, this.reconnectDelay);
    } else {
      const isDevEnvironment = process.env.REACT_APP_ENVIRONMENT === "dev";
      isDevEnvironment && console.log("Max reconnect attempts reached");
      // Out of retries: the connection is gone for reasons we cannot fix here, so prompt for
      // a re-login instead of clearing the session out from under the user.
      notifySessionDisconnected();
    }
  }

  public async send(event: string, data?: any): Promise<void> {
    const isDevEnvironment = process.env.REACT_APP_ENVIRONMENT === "dev";

    await this.ensureValidPublicToken();

    const pipelinePayload = addPipelineToSocketPayload(
      event,
      data,
      window.location.search,
    );
    const payload = addScanModeToSocketPayload(event, pipelinePayload);
    if (this.socketRef) this.socketRef.emit(event, payload);
    else isDevEnvironment && console.log("send: socket not connected");
  }

  public async cancelAskAiRequest(requestId: string): Promise<void> {
    const isDevEnvironment = process.env.REACT_APP_ENVIRONMENT === "dev";

    await this.ensureValidPublicToken();

    if (this.socketRef) {
      this.socketRef.emit("cancel_ask_ai", { requestId });
      isDevEnvironment && console.log(`Cancelled AI request: ${requestId}`);
    } else {
      isDevEnvironment && console.log("cancel_ask_ai_request: socket not connected");
    }
  }

  private async ensureValidPublicToken(): Promise<void> {
    const isDevEnvironment = process.env.REACT_APP_ENVIRONMENT === "dev";

    // If we're using a public token, check if it's expired before proceeding
    if (!localStorage.getItem("JMLAuthToken")) {
      const pathName = window.location.pathname;
      const canvasMatch = pathName.match(/\/canvas\/([^/]+)/);
      const slugOrIdFromUrl = canvasMatch ? canvasMatch[1] : null;
      const isPublic = slugOrIdFromUrl ? isPublicSlug(slugOrIdFromUrl) : false;

      if (isPublic) {
        const expiryTime = localStorage.getItem("JMLPublicTokenExpiry");
        const currentTime = Date.now();
        const expiryMs = expiryTime !== null ? Number(expiryTime) : NaN;
        const isInvalidOrExpiring = !Number.isFinite(expiryMs) || currentTime > (expiryMs - 5000);

        if (!localStorage.getItem("JMLPublicToken") || isInvalidOrExpiring) {
          isDevEnvironment && console.log("Public token expired, refreshing...");
          // Clear old token before refresh to avoid sending stale credentials if refresh fails
          localStorage.removeItem("JMLPublicToken");
          localStorage.removeItem("JMLPublicTokenExpiry");
          const newToken = await fetchAndStorePublicToken();
          if (newToken) {
            this.connect();
          }
        }
      }
    }
  }

  public close(): void {
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    // Drop the token-refresh subscription before the singleton is discarded: this instance must not
    // be revived by a refresh that happens after it has been retired.
    if (this.unsubscribeTokenRefresh) {
      this.unsubscribeTokenRefresh();
      this.unsubscribeTokenRefresh = null;
    }
    // Detach before disconnecting: socket.io emits "disconnect" synchronously, and that handler
    // would otherwise read this deliberate close as a lost connection and start reconnecting. Its
    // `this.socketRef !== socket` guard makes it a no-op once the reference is cleared.
    const closingSocket = this.socketRef;
    this.socketRef = null;
    if (closingSocket) closingSocket.disconnect();
    WebSocketInstance.instance = null;
  }

  private attachCustomListeners(): void {
    if (!this.socketRef) return;
    for (const [type, listeners] of Object.entries(this.customListeners)) {
      listeners.forEach((listener) => {
        this.socketRef!.off(type, listener);
        this.socketRef!.on(type, listener);
      });
    }
  }

  public addEventListener(type: string, listener: (...args: any[]) => void): void {
    if (!this.customListeners[type]) {
      this.customListeners[type] = [];
    }
    if (this.customListeners[type].includes(listener)) {
      return;
    }
    this.customListeners[type].push(listener);
    if (this.socketRef) {
      this.socketRef.on(type, listener);
    }
  }

  public removeEventListener(type: string, listener: (...args: any[]) => void): void {
    if (this.customListeners[type]) {
      const index = this.customListeners[type].indexOf(listener);
      if (index > -1) {
        this.customListeners[type].splice(index, 1);
      }
    }
    if (this.socketRef) {
      this.socketRef.off(type, listener);
    }
  }

  public get isSocketOpen() {
    return !!this.socketRef;
  }

  public get isConnected(): boolean {
    return !!(this.socketRef && (this.socketRef as any).connected === true);
  }
}

export default WebSocketInstance;
